import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { background as backgroundColor } from 'theme/palette'
import { borderWidth } from 'theme/globals'
import typography from 'theme/typography'
import { KEYS, CONTROLS } from 'utils/constants'
import Type from 'components/Type'
import Label from 'components/Label'
import Stack from '@mui/material/Stack'

const InputField = styled.div.withConfig({
  shouldForwardProp: prop => ![
    'textAlign',
    'labelPlacement',
    'labelAlign',
    'fluid',
    'maxWidth',
  ].includes(prop),
})`
  text-align: ${props => props.textAlign};
  display: inline-grid;
  grid-gap: 10px;
  position: relative;
  grid-auto-flow: ${props =>
    props.labelPlacement === 'top' ? 'row' : 'column'};
  align-items: ${props => props.labelAlign};
  ${props => (props.fluid ? `width: 100%;` : '')}
  ${props => (props.maxWidth ? `width: ${props.maxWidth};` : '')}
`

const StyledInput = styled.textarea.withConfig({
  shouldForwardProp: prop => ![
    'noPad',
    'fluid',
    'variant',
    'size',
    'noResize',
    'font',
    'textAlign',
    'bgColor',
    'variant',
    'border',
    'borderWidth',
    'isReadOnly',
  ].includes(prop),
})`
  ${props => (props.noPad ? '' : props.theme.internalMixins.p([1]))}
  ${props => (props.fluid ? `width: 100%;` : '')}
  ${props =>
    props.variant === 'default'
      ? `border-radius: ${props.theme.globals.borderRadius.more}px;`
      : ''}
  min-height: ${props => (props.size === 'short' ? '45px' : '90px')};
  ${props => (props.noResize ? 'resize: none;' : '')}
  ${props => props.font && props.theme.typography[props.font]}
  text-align: ${props => props.textAlign};
  -webkit-touch-callout: none;
  outline: 0;
  min-width: 1px;
  background-color: ${props =>
    props.bgColor
      ? props.theme.palette.background[props.bgColor].main
      : props.variant === 'default'
      ? props.theme.palette.background.paper.main
      : 'transparent'};
  border: ${props => props.theme.globals.border[props.border]};
  border-width: ${props => props.theme.globals.borderWidth[props.borderWidth]};
  overflow: hidden;
  ${props =>
    props.variant === 'line'
      ? `
    border-top: none;
    border-left: none;
    border-right: none;
    border-width: ${props.theme.globals.borderWidth.thick};
  `
      : ''}
  transition: ${props =>
    props.theme.internalMixins.setTransition(['border-color'])};

  &:hover {
    ${props =>
      !props.isReadOnly &&
      `
      border-color: ${props.theme.palette.border.neutralCool_600};
    `}
  }

  &:focus {
    ${props =>
      !props.isReadOnly &&
      `
      border-color: ${props.theme.palette.border.tertiary};
    `}
  }

  ${props =>
    props.isReadOnly &&
    `
    pointer-events: none;
  `}

  &[type="search"]::-webkit-search-cancel-button {
    // -webkit-appearance: none;
    // height: 10px;
    // width: 10px;
    // background: red;
  }

  &[type="search"]::placeholder {
    color: ${props => props.theme.palette.text.neutralCool_700};
  }
  
`

const Input = ({
  onChange,
  onEnter,
  onTab,
  onDelete,
  value,
  readOnly,
  id,
  autoSize,
  charCountRight,
  ...props
}) => {
  const [inputID] = useState(id || Math.random().toString())
  const [charCount, setCharCount] = useState(0)
  const [height, setHeight] = useState(20)
  const ref = useRef(null)
  const refDefined = !!ref?.current

  useEffect(() => {
    setCharCount(value.length)
  }, [value])

  const onChangeHandler = e => {
    let val = e.target.value
    if (props.charCount) val = val.substring(0, props.charCount)
    onChange(val)
  }

  const onFocusHandler = () => {
    if (props.selectOnFocus) document.getElementById(id).select()
  }

  const onBlurHandler = () => {}

  const onKeyDownHandler = e => {
    if (
      props.maxLength &&
      e.target.value.length >= props.maxLength &&
      !(e.keyCode === KEYS.DELETE_FORWARD || e.keyCode === KEYS.DELETE) &&
      !CONTROLS.ARROWS.includes(e.keyCode) &&
      e.keyCode !== KEYS.TAB
    )
      e.preventDefault()
    if (e.keyCode === KEYS.DELETE) onDelete(e)
    if (e.keyCode === KEYS.ENTER) onEnter(e)
    if (e.keyCode === KEYS.TAB) onTab(e)
    if (e.key === value) onNoChange(e)
  }

  const renderInput = () => {
    const notMaskProps = {
      readOnly,
      value,
      onFocus: onFocusHandler,
      onBlur: onBlurHandler,
      onChange: onChangeHandler,
    }
    return (
      <StyledInput
        id={inputID}
        ref={ref}
        {...props}
        {...notMaskProps}
        placeholder={props.placeholder ? props.placeholder : ''}
        onKeyDown={onKeyDownHandler}
        style={{ height: `${height}px` }}
      />
    )
  }

  useEffect(() => {
    if (autoSize && refDefined) setHeight(ref.current.scrollHeight || 20)
  }, [value, refDefined])

  return (
    <InputField {...props}>
      {(props.label || props.optional) && (
        <Label>
          {props.label}{' '}
          {props.optional ? (
            <Type
              variant="labelSubAtomicMedium"
              inline
              style={{ textTransform: 'none', letterSpacing: 0 }}
            >
              (optional)
            </Type>
          ) : (
            ''
          )}
        </Label>
      )}
      <Stack display="inline-grid" gap={1} alignItems="center" position="relative">
        {renderInput()}
      </Stack>
      {props.charCount && (
        <Type variant="labelAtomic" color="neutralCool_700" textAlign="right">
          {charCount}/{props.charCount}
        </Type>
      )}
    </InputField>
  )
}

Input.propTypes = {
  id: PropTypes.string,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.string,
  border: PropTypes.string,
  borderWidth: PropTypes.oneOf(Object.keys(borderWidth)),
  fluid: PropTypes.bool,
  size: PropTypes.oneOf(['short', 'default', 'tall']),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  labelPlacement: PropTypes.oneOf(['top', 'left', 'right']),
  labelAlign: PropTypes.oneOf(['flex-start', 'center', 'flex-end']),
  variant: PropTypes.oneOf(['default']),
  bgColor: PropTypes.oneOf(Object.keys(backgroundColor)),
  font: PropTypes.oneOf(Object.keys(typography)),
  maxWidth: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  optional: PropTypes.bool,
  charCount: PropTypes.number,
  inputProps: PropTypes.object,
  selectOnFocus: PropTypes.bool,
  noPad: PropTypes.bool,
  onChange: PropTypes.func,
  onEnter: PropTypes.func,
  onTab: PropTypes.func,
  onDelete: PropTypes.func,
  autoSize: PropTypes.bool,
  textAlign: PropTypes.string,
  noResize: PropTypes.bool,
}

Input.defaultProps = {
  color: 'secondary',
  border: 'main',
  borderWidth: 'thick',
  outlined: true,
  size: 'default',
  labelPlacement: 'top',
  labelAlign: 'center',
  variant: 'default',
  type: 'text',
  font: 'bodySmall',
  textAlign: 'left',
  onChange: () => {},
  onEnter: () => {},
  onTab: () => {},
  onDelete: () => {},
}

export default Input
